import { Button } from '../Button/Button';
import ArrowLeft from '@app/assets/icons/arrow-left.svg?react';
import ArrowRight from '@app/assets/icons/arrow-right.svg?react';
import { useEffect, useState } from 'react';
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg?react';

export const Pagination = ({
  page = 1,
  total = 0,
  handleNextPage,
  handlePrevPage,
  handleLimitChange,
}: {
  page?: number;
  total?: number;
  handleNextPage: (page: number) => void;
  handlePrevPage: (page: number) => void;
  handleLimitChange: (limit: number) => void;
}) => {
  const [limit, setLimit] = useState('10');
  const [currentPage, setCurrentPage] = useState(page);
  const numberOfPages = Math.ceil(total / Number(limit));

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) newPage = 1;
    setCurrentPage(newPage);
    if (newPage > page) {
      handleNextPage(newPage);
    } else {
      handlePrevPage(newPage);
    }
  };

  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  return (
    <div className="flex justify-between px-4 py-2 flex-wrap gap-2">
      <div className="flex gap-2 items-center flex-wrap md:flex-nowrap">
        <span className="text-base-davyGray text-sm whitespace-nowrap">
          Page
        </span>
        <input
          className="bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 min-w-fit w-fit"
          type="number"
          name="page"
          min={1}
          max={numberOfPages}
          value={currentPage}
          onKeyDown={(e) => e.preventDefault()} // Prevent typing
          onChange={(e) => {
            const value = Number(e.target.value);
            if (value <= numberOfPages && value > 0) {
              setCurrentPage(value);
              handlePageChange(value);
            }
          }}
        />
        <span className="text-base-davyGray text-sm whitespace-nowrap">
          of {numberOfPages}
        </span>
        <Button
          kind="quaternary"
          className="min-w-fit"
          disabled={currentPage === 1}
          leftNode={<ArrowLeft className="-mr-2" />}
          onClick={() =>
            handlePageChange(currentPage - 1 === 0 ? 1 : currentPage - 1)
          }
        />
        <Button
          kind="quaternary"
          onClick={() =>
            handlePageChange(
              currentPage + 1 > numberOfPages ? currentPage : currentPage + 1
            )
          }
          disabled={currentPage === numberOfPages}
          type="button"
          rightNode={<ArrowRight />}
        >
          Next page
        </Button>
      </div>
      <div className="flex items-center gap-2 text-base-davyGray flex-wrap md:flex-nowrap">
        <span className="text-sm whitespace-nowrap">Show</span>
        <div className="relative w-20">
          <Combobox
            immediate
            value={limit}
            onChange={(value: string | null) => {
              if (value) {
                handleLimitChange(Number(value));
                setLimit(value);
              }
            }}
          >
            <div className="relative w-full">
              <ComboboxInput
                displayValue={(item: string) => item}
                className="bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 pr-10 text-center"
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </div>
            <ComboboxOptions
              anchor="top"
              className="absolute left-0 z-[9999] w-20 mb-2 rounded-lg shadow-lg bg-white max-h-64 overflow-y-auto"
            >
              {[
                { label: '10', value: '10' },
                { label: '20', value: '20' },
                { label: '50', value: '50' },
                { label: '100', value: '100' },
              ].map((option) => (
                <ComboboxOption
                  key={option.value}
                  value={option.value}
                  className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt text-center"
                >
                  {option.label}
                </ComboboxOption>
              ))}
            </ComboboxOptions>
          </Combobox>
        </div>
        <span className="flex-shrink-0 text-sm whitespace-nowrap">
          per page
        </span>
      </div>
    </div>
  );
};
